import { DateInput3 } from '@blueprintjs/datetime2';
import {
    DesignerPickerItemResource,
    TaskSubTicketResource,
} from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import DevText from '@app/components/Text';
import { $task } from '@app/containers/pages/Task/store/states';
import { useForm, useRichTextFormField, useTextArrayFormField } from '@app/hooks';
import { getRichStringRequiredValidator, getStringMaxLengthValidator } from '@app/hooks/validation/functions';
import { Button, Callout, Card, Divider, FormGroup, Intent } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import RichEditorFormField from '@components/RichEditorFormField';
import RichText from '@components/RichText';
import { ModalProps } from '@modals/types';

import { fetchDesignerPickerItems, updateSubTicket, updateSubTicketAssignee } from '../../store/effects';
import {
    SubTicketRecoveryScope,
    reconcileSubTicketRefs,
    refreshSubTicketViews,
    reportSubTicketMutation,
    showSubTicketFailure,
    showSubTicketPartialSuccess,
    showSubTicketRecoveryOffer,
} from '../../store/reconcile';
import { designerPickerKey, nextGeneration } from '../../store/requestGeneration';
import { $designerPicker } from '../../store/states';
import {
    MAX_EXPECTED_DELIVERABLE_LENGTH,
    resolveDesignerPicker,
    resolveDueWarning,
    resolveSubTicketConflict,
} from '../../utils';
import { SubTicketDueWarning } from '../../utils';
import DeliverableListField from '../components/DeliverableListField';
import DesignerPickerField from '../components/DesignerPickerField';

import { submitSubTicketUpdate } from './submitUpdate';

export interface UpdateSubTicketModalProps {
    subTicket: TaskSubTicketResource;

    // UPDATE and ASSIGNEE_MANAGE are independent. A holder of UPDATE alone never sees the
    // Designer field; a holder of ASSIGNEE_MANAGE alone never sees an editable Scope.
    canUpdate: boolean;
    canManageAssignee: boolean;

    isArchivedShown: boolean;
    expandedSubTicketId: ID | null;
}

type Props = ModalProps<UpdateSubTicketModalProps>;

const deliverableValidators = [getStringMaxLengthValidator({ maxStringLength: MAX_EXPECTED_DELIVERABLE_LENGTH })];
const scopeValidators = [getRichStringRequiredValidator({ richStringRequiredMessage: 'Scope is required' })];

const UpdateSubTicketModal: React.FC<Props> = ({ closeModal, data }) => {
    const task = useStore($task);
    const designerPicker = useStore($designerPicker);

    const subTicket = data?.subTicket;
    const canUpdate = data?.canUpdate ?? false;
    const canManageAssignee = data?.canManageAssignee ?? false;

    // Validators are attached only when the field is actually editable, so an unrendered field
    // can never block a submission it has no part in
    const scope = useRichTextFormField({
        id: 'scope',
        validators: canUpdate ? scopeValidators : [],
        initialValue: subTicket?.scope ?? '',
    });

    const expectedDeliverables = useTextArrayFormField({
        id: 'expected-deliverables',
        validators: canUpdate ? deliverableValidators : [],
        initialValue: subTicket?.expected_deliverables ?? [],
    });

    // A phase with no due date reopens with an empty picker, never with a fabricated one
    const [dueDate, setDueDate] = useState<Date | null>(
        subTicket && subTicket.due_at ? moment(subTicket.due_at).toDate() : null
    );
    const [selectedDesigner, setSelectedDesigner] = useState<DesignerPickerItemResource | null>(null);

    const [dueWarning, setDueWarning] = useState<SubTicketDueWarning | null>(null);
    const [isDueWarningConfirmed, setIsDueWarningConfirmed] = useState(false);
    const [conflictMessage, setConflictMessage] = useState<string | null>(null);

    // Fetched only when the Designer field exists. Without the assignee permission the picker
    // is never read, so a stale global picker store cannot influence this modal at all.
    useEffect(() => {
        if (!subTicket || !canManageAssignee) {
            return;
        }

        fetchDesignerPickerItems({
            filter: { task_id: subTicket.task_id },
            generation: nextGeneration(designerPickerKey(subTicket.task_id)),
        }).catch((e) => {
            showSubTicketFailure('The designer list could not be loaded.');
            console.error(e);
        });
    }, [subTicket?.task_id, canManageAssignee]);

    // One gate between the global picker store and this modal. Items held for another Request are
    // withheld outright, and a selection that no longer matches anything on offer is dropped
    // rather than carried across Requests.
    const picker = resolveDesignerPicker(designerPicker, subTicket?.task_id ?? '', selectedDesigner);

    // Track the seeded phase so a deliberate clear is not immediately undone.
    const seededDesignerSubTicketIdRef = useRef<ID | null>(null);

    // Seed with the designer already on the phase, but only from THIS Request's picker
    useEffect(() => {
        if (
            !subTicket ||
            !canManageAssignee ||
            !picker.items ||
            picker.selected !== null ||
            seededDesignerSubTicketIdRef.current === subTicket.id
        ) {
            return;
        }

        const current = picker.items.find((item) => item.user.id === subTicket.assignee_user.id);
        if (current) {
            seededDesignerSubTicketIdRef.current = subTicket.id;
            setSelectedDesigner(current);
        }
    }, [picker.items, picker.selected, subTicket?.id, subTicket?.assignee_user.id, canManageAssignee]);

    useEffect(() => {
        setDueWarning(null);
        setIsDueWarningConfirmed(false);
    }, [dueDate]);

    const form = useForm({
        // Only editable fields participate in validation
        fields: canUpdate ? [scope, expectedDeliverables] : [],

        apiCall: async () => {
            try {
                if (!subTicket) {
                    return { response: false };
                }

                // An editor may add a due date, change it, or clear it back to none. Cleared
                // means null on the wire, not "leave whatever was there" — otherwise clearing
                // the picker would silently be a no-op.
                //
                // A reassign-only caller edits no fields at all and never sends this endpoint,
                // so the phase keeps exactly the due date it already had, null included.
                const dueAt = canUpdate
                    ? dueDate
                        ? moment.utc(moment(dueDate).endOf('day').toDate()).format()
                        : null
                    : subTicket.due_at;

                if (canUpdate) {
                    const warning = resolveDueWarning({
                        dueAt,
                        parentDeadlineAt: task?.deadline_at ?? null,
                        nowMs: Date.now(),
                    });

                    if (warning && !isDueWarningConfirmed) {
                        setDueWarning(warning);
                        return { response: false };
                    }
                }

                setConflictMessage(null);

                // Named `recovery` rather than `scope`: `scope` is the rich-text field above,
                // and shadowing it here would silently send the wrong value to the server.
                const recovery: SubTicketRecoveryScope = {
                    what: 'The sub-task',
                    taskId: subTicket.task_id,
                    refreshViews: () =>
                        refreshSubTicketViews({
                            taskId: subTicket.task_id,
                            isArchivedShown: data!.isArchivedShown,
                            expandedSubTicketId: data!.expandedSubTicketId,
                        }),
                };

                const result = await submitSubTicketUpdate({
                    subTicket,
                    capabilities: { canUpdate, canManageAssignee },
                    values: {
                        scope: scope.value,
                        expectedDeliverables: expectedDeliverables.value,
                        dueAt,
                    },
                    selectedDesigner: picker.selected,
                    deps: {
                        updateSubTicket: (id, input) => updateSubTicket({ id, input }),
                        updateSubTicketAssignee: (id, input) => updateSubTicketAssignee({ id, input }),
                        reconcileRefs: reconcileSubTicketRefs,
                        refreshViews: recovery.refreshViews,
                        resolveConflict: resolveSubTicketConflict,
                    },
                });

                if (result.conflictMessage) {
                    setConflictMessage(result.conflictMessage);
                    return { response: false };
                }

                if (result.blockedReason) {
                    return { response: false };
                }

                // Both surfaces report their own truth. Participants Retry re-reads the parent
                // Task; View Retry re-reads the views. They repair different things and are
                // never conflated.
                //
                // `views` is null only on the paths that returned above, so nothing is ever
                // reported against an invented refresh outcome.
                const views = result.views;

                if (!views) {
                    closeModal?.();

                    return { response: result.ok };
                }

                if (result.partial) {
                    // One endpoint landed and the other did not. The partial toast states which,
                    // and carries the view truth with it rather than assuming the list is current
                    showSubTicketPartialSuccess(result.partial, views, recovery);

                    if (result.reconciled?.participantsRefreshed === false) {
                        showSubTicketRecoveryOffer('participants', recovery);
                    }
                } else if (result.didUpdate || result.didReassign) {
                    reportSubTicketMutation({
                        successMessage:
                            result.didUpdate && result.didReassign
                                ? 'Sub-task updated and designer reassigned.'
                                : result.didReassign
                                  ? 'Designer reassigned.'
                                  : 'Sub-task updated.',
                        views,
                        reconciled: result.reconciled,
                        scope: recovery,
                    });
                }

                closeModal?.();

                return { response: result.ok };
            } catch (e) {
                throw e;
            }
        },
        onFailure: (error) => {
            showSubTicketFailure('The sub-task could not be saved. Please try again.');
            console.error(error);
        },
    });

    if (!data || !subTicket) {
        return null;
    }

    // Neither capability means there is nothing to edit and no modal to show
    if (!canUpdate && !canManageAssignee) {
        return null;
    }

    // Whenever the Designer field is on screen a valid designer FROM THIS REQUEST must be
    // selected, whichever other permissions the caller holds. A picker that is empty, still
    // loading, failed, or belongs to another Request offers nothing valid, so Save stays disabled
    // rather than quietly saving the fields and leaving the assignment unmanaged.
    // The due date is optional, so it is not a reason to keep Save disabled
    const isSubmitDisabled = form.hasFieldErrors || (canManageAssignee && picker.selected === null);

    // Read-only context for a reassign-only caller: they must see what they are moving without
    // being offered any way to change it
    const renderPhaseContext = () => (
        <div className="mb-2">
            <FormGroup label="Scope">
                <Card compact>
                    <RichText value={subTicket.scope} />
                </Card>
            </FormGroup>

            <FormGroup label="Expected deliverables">
                {subTicket.expected_deliverables.length === 0 ? (
                    <DevText muted>None recorded</DevText>
                ) : (
                    <ol className="mt-0 mb-0">
                        {subTicket.expected_deliverables.map((deliverable, index) => (
                            <li key={`${index}-${deliverable}`}>
                                <DevText inline>{deliverable}</DevText>
                            </li>
                        ))}
                    </ol>
                )}
            </FormGroup>

            <FormGroup label="Due date">
                {subTicket.due_at === null ? (
                    <DevText muted>No due date</DevText>
                ) : (
                    <DevText>{moment(subTicket.due_at).format('D MMM YYYY')}</DevText>
                )}
            </FormGroup>
        </div>
    );

    const renderEditableFields = () => (
        <>
            {/* 1 — Scope */}
            <RichEditorFormField
                field={scope}
                formGroupProps={{ label: 'Scope' }}
                richEditorProps={{ placeholder: 'Describe the scope of this phase' }}
            />

            {/* 2 — Expected deliverables */}
            <DeliverableListField field={expectedDeliverables} />

            {/* 3 — Due date, optional and clearable */}
            <FormGroup label="Due date" labelInfo="(optional)">
                <DateInput3
                    fill
                    highlightCurrentDay
                    canClearSelection
                    placeholder="No due date"
                    value={dueDate ? moment(dueDate).format() : null}
                    dayPickerProps={{ firstWeekContainsDate: 1 }}
                    formatDate={(date) => moment(date).format('MMM Do, YYYY (dddd)')}
                    parseDate={(str) => new Date(str)}
                    onChange={(newDate) => setDueDate(newDate ? new Date(newDate) : null)}
                />
            </FormGroup>
        </>
    );

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">
                        {canUpdate ? 'Update' : 'Reassign'} sub-task #{subTicket.ordinal}
                    </Heading>
                    <Button minimal icon="cross" onClick={closeModal} />
                </Flex>

                <Divider className="mb-2" />

                <form onSubmit={form.handleFormSubmit}>
                    {canUpdate ? renderEditableFields() : renderPhaseContext()}

                    {/* 4 — Designer assignment, only for a holder of the assignee permission */}
                    {canManageAssignee && (
                        <>
                            <DesignerPickerField
                                items={picker.items}
                                selected={picker.selected}
                                onSelect={setSelectedDesigner}
                                onClear={() => setSelectedDesigner(null)}
                            />

                            {picker.hasFailed && (
                                <Callout
                                    className="mb-2"
                                    intent={Intent.DANGER}
                                    title="Designers could not be loaded"
                                >
                                    <DevText>
                                        The list of assignable designers is unavailable, so this phase cannot be
                                        saved right now. Reload the page to try again.
                                    </DevText>
                                </Callout>
                            )}
                        </>
                    )}

                    {dueWarning && (
                        <Callout className="mt-2" intent={Intent.WARNING} title="Check the due date">
                            <DevText>{dueWarning.message}</DevText>
                            <DevText className="mt-1" muted>
                                You can still save it — confirm to continue.
                            </DevText>

                            <Button
                                className="mt-1"
                                intent={Intent.WARNING}
                                disabled={isDueWarningConfirmed}
                                onClick={() => setIsDueWarningConfirmed(true)}
                            >
                                {isDueWarningConfirmed ? 'Confirmed' : 'Confirm due date'}
                            </Button>
                        </Callout>
                    )}

                    {conflictMessage && (
                        <Callout className="mt-2" intent={Intent.DANGER} title="Cannot reassign right now">
                            <DevText>{conflictMessage}</DevText>
                        </Callout>
                    )}

                    <Flex className="mt-2" justify="flex-end">
                        <Button className="mr-1" outlined onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button
                            disabled={isSubmitDisabled}
                            loading={form.isSubmitting}
                            type="submit"
                            intent={Intent.PRIMARY}
                        >
                            Save
                        </Button>
                    </Flex>
                </form>
            </Card>
        </Overlay>
    );
};

export default UpdateSubTicketModal;
